// 電腦版 AI 的判斷（ls-loop ME TE.4；對齊 Android lscore.EventAi / pushkit.AiText）。
// 判斷原則：只算真的在現場的人 / 寵物 / 車 / 包裹；<類別>_depicted = 只在螢幕、照片、海報、雕像、玩偶等，不是真的。
import type { TFunction } from 'i18next';

export interface EventAi {
  /** confirmed（有真的）/ false_positive（可能誤報）/ uncertain */
  verdict: string;
  labels: { label: string; score: number }[];
  at_us?: number;
}

const CLASSES = ['person', 'pet', 'vehicle', 'package'] as const;
type AiClass = (typeof CLASSES)[number];
const isClass = (s: string): s is AiClass => (CLASSES as readonly string[]).includes(s);

/** 「AI：有人、寵物」/「AI：可能誤報（螢幕或照片裡的人）」/「AI：無法確定」；沒有可顯示的回 null。 */
export function aiSummary(t: TFunction, ai: EventAi | null | undefined): string | null {
  if (!ai) return null;
  const sep = t('ai.sep');
  const names = (ai.labels ?? []).map((l) => l.label);
  let text: string | null = null;
  if (ai.verdict === 'confirmed') {
    const real = [...new Set(names.filter(isClass).map((c) => t(`ai.${c}`)))];
    text = real.length ? real.join(sep) : null;
  } else if (ai.verdict === 'false_positive') {
    const why = [
      ...new Set(
        names
          .filter((n) => n.endsWith('_depicted'))
          .map((n) => n.slice(0, -'_depicted'.length))
          .filter(isClass)
          .map((c) => t(`ai.depicted.${c}`)),
      ),
    ];
    const fp = t('ai.falsePositive');
    text = why.length ? t('ai.withReason', { verdict: fp, reason: why.join(sep) }) : fp;
  } else if (ai.verdict === 'uncertain') {
    text = t('ai.uncertain');
  }
  return text ? t('ai.summary', { text }) : null;
}

/** 門鈴沒人接時雲端本地 AI 看按鈴截圖寫回的說明（不是判真假，跟 verdict 那套分開）。 */
export interface RingDesc {
  people: number;
  delivery?: boolean;
  /** box / bag / flowers / other / nothing */
  holding?: string;
  package?: boolean;
  /** clear / partial / unclear */
  visibility?: string;
  at_us?: number;
}

const HOLDING = ['box', 'bag', 'flowers', 'other'] as const;

/** 按鈴說明一句話（不含「AI：」）。對齊雲端 cloud/events/.../service/ringdesc_text.go RingDescText，改那邊要一起改。 */
export function ringDescText(t: TFunction, d: RingDesc): string {
  if (d.visibility === 'unclear') return t('ai.ringDesc.unclear');
  const parts: string[] = [];
  const n = typeof d.people === 'number' && d.people > 0 ? Math.floor(d.people) : 0;
  if (n === 0) {
    parts.push(t('ai.ringDesc.nobody'));
  } else {
    parts.push(t(n === 1 ? 'ai.ringDesc.peopleOne' : 'ai.ringDesc.peopleMany', { n }));
    if (d.delivery) parts.push(t('ai.ringDesc.delivery'));
    if (d.holding && (HOLDING as readonly string[]).includes(d.holding)) parts.push(t(`ai.ringDesc.holding.${d.holding}`));
  }
  if (d.package) parts.push(t('ai.ringDesc.package'));
  return parts.join(t('ai.ringDesc.sep'));
}

/** 事件列的 AI 小字：有按鈴說明（ai_desc）就顯示它，否則用 verdict 的 aiSummary。confirmed 只給 verdict 那套上色用。 */
export function eventAiLine(
  t: TFunction,
  ev: { ai?: EventAi | null; ai_desc?: RingDesc | null } | null | undefined,
): { text: string; confirmed: boolean } | null {
  if (ev?.ai_desc) return { text: t('ai.summary', { text: ringDescText(t, ev.ai_desc) }), confirmed: false };
  const text = aiSummary(t, ev?.ai);
  return text ? { text, confirmed: ev?.ai?.verdict === 'confirmed' } : null;
}

/** 電腦版 AI（型號 edge-ai）不是攝影機。 */
export const EDGE_AI_MODEL = 'edge-ai';
export function isEdgeAi(d: { model?: string | null }): boolean {
  return d.model === EDGE_AI_MODEL;
}

/** AI 電腦超過這麼久沒聯絡就當離線：雲端的 online.state 可能是舊的（2026-10-11：雲端搬 .111 時複製過去的
 *  「在線」一直沒更新，m2mini 斷線一天網頁還寫在線）。正常連線時最後聯絡都在幾十秒內。 */
export const EDGE_STALE_US = 5 * 60 * 1_000_000;

type EdgeLike = { online?: { state?: string; last_seen?: number } | null };

/** AI 電腦真的在線：狀態在線，而且最後聯絡在 5 分鐘內（沒有最後聯絡時間 → 只能信狀態）。 */
export function edgeOnline(d: EdgeLike, nowUs: number = Date.now() * 1000): boolean {
  if (d.online?.state !== 'online') return false;
  const seen = d.online.last_seen;
  return typeof seen !== 'number' || nowUs - seen <= EDGE_STALE_US;
}

/** 有 AI 電腦、而且全部離線 → 事件頁提示。沒有 AI 電腦 = false。 */
export function edgeAiAllOffline(edges: EdgeLike[], nowUs?: number): boolean {
  return edges.length > 0 && edges.every((d) => !edgeOnline(d, nowUs));
}
